import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import PlotlyPlot from 'react-plotly.js'
import moment from 'moment-timezone'

import alertsPlotBandBuilder from 'pages/Device/ThermostatRthPanel/Chart/alertsPlotBandBuilder'
import { updateLayoutOnResetZoom, updateLocalStorageAndGetAllTracesToDisplay } from 'pages/Device/ThermostatRthPanel/Chart/plotHelper'
import { parseRthData } from 'pages/Device/ThermostatRthPanel/Chart/rthDataParser'
import { setYaxisRange } from 'pages/Device/ThermostatRthPanel/Chart/chartOptions'

import legendDataParser from 'pages/Device/ThermostatRthPanel/Chart/legendDataparser'
import AlertsTooltip from 'pages/shared/AlertsTooltip'

import { config } from 'helpers/plotly/config'

import { NoData, screenSize } from 'styles/shared'

const propTypes = {
  chartDivId: PropTypes.string.isRequired,
  deviceId: PropTypes.string.isRequired,
  startTime: PropTypes.string.isRequired,
  endTime: PropTypes.string.isRequired,
  timeZone: PropTypes.string.isRequired,
  isZoomed: PropTypes.bool.isRequired,
  plotlyRevision: PropTypes.number.isRequired,
  freezeMouseHover: PropTypes.bool,
  freezeMouseHoverCallback: PropTypes.func,
  onHoverCallback: PropTypes.func.isRequired,
  onUnhoverCallback: PropTypes.func.isRequired,
  onRelayoutCallback: PropTypes.func.isRequired,
  onClickCallback: PropTypes.func,
  onDoubleClickCallback: PropTypes.func,
  onLegendDataCallback: PropTypes.func,
  variant: PropTypes.string,
  updateGraphElementCallback: PropTypes.func,
  selectedLegends: PropTypes.shape(),
  rthData: PropTypes.shape()
}

const defaultProps = {
  rthData: null,
  selectedLegends: null,
  variant: 'normal',
  onClickCallback: undefined,
  onDoubleClickCallback: undefined,
  freezeMouseHover: false,
  freezeMouseHoverCallback: undefined,
  onLegendDataCallback: undefined,
  updateGraphElementCallback: undefined
}

const RthPlot = ({
  deviceId,
  chartDivId,
  startTime,
  endTime,
  timeZone,
  rthData,
  plotlyRevision,
  isZoomed,
  freezeMouseHover,
  onHoverCallback,
  onUnhoverCallback,
  onRelayoutCallback,
  onLegendDataCallback,
  onClickCallback,
  onDoubleClickCallback,
  updateGraphElementCallback,
  variant = 'normal',
  selectedLegends
}) => {
  const { traces, zoneTraces, stageTraces, layout, alerts, deviceConnectionStateAlerts } = useMemo(() => {
    return parseRthData(rthData, timeZone, startTime, endTime)
  }, [startTime, endTime, timeZone, rthData])

  const graphRef = useRef(null)

  const allTraces = useMemo(() => {
    const data = updateLocalStorageAndGetAllTracesToDisplay(deviceId, selectedLegends, traces, zoneTraces, stageTraces)

    return data
  }, [deviceId, selectedLegends, stageTraces, traces, zoneTraces])

  const updatedLayout = useMemo(() => {
    if (!layout) { return layout }

    const updatedLayout = updateLayoutOnResetZoom(isZoomed, graphRef.current?._fullLayout, layout) // resets the zoom
    setYaxisRange(allTraces, updatedLayout)
    updatedLayout.xaxis.fixedrange = freezeMouseHover
    updatedLayout.yaxis5.fixedrange = freezeMouseHover
    updatedLayout.yaxis6.fixedrange = freezeMouseHover

    return updatedLayout
  }, [layout, isZoomed, freezeMouseHover, plotlyRevision, selectedLegends, allTraces]) // eslint-disable-line react-hooks/exhaustive-deps

  const xaxisRange = JSON.stringify(updatedLayout?.xaxis?.range || '')

  const updateAlerts = useCallback((selectedLegendAlerts) => {
    alertsPlotBandBuilder.addOrUpdateAlerts('rth-alerts-group', updatedLayout, chartDivId, alerts, selectedLegendAlerts, deviceConnectionStateAlerts)
  }, [xaxisRange, chartDivId, alerts]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (graphRef.current != null) {
      updateAlerts(selectedLegends?.alerts)
    }
  }, [plotlyRevision, selectedLegends?.alerts, updateAlerts, isZoomed])

  useEffect(() => {
    if (onLegendDataCallback && updatedLayout) {
      onLegendDataCallback(legendDataParser.parseLegendData(allTraces, rthData), legendDataParser.alertsWithinVisibleRange(updatedLayout, alerts), deviceConnectionStateAlerts)
    }
  }, [allTraces, updatedLayout, rthData, alerts, onLegendDataCallback])

  const closestPointToCursor = (e) => {
    return e.points.reduce((prev, curr) => Math.abs(curr.bbox.x0 - e.event.layerX) < Math.abs(prev.bbox.x0 - e.event.layerX) ? curr : prev)
  }

  const timeAtCursor = (e) => {
    e.event.preventDefault()
    const closestPoint = closestPointToCursor(e)
    if (e.points.length > 0 && closestPoint.x) {
      return moment.tz(closestPoint.x, timeZone).unix() * 1000
    }

    return null
  }

  const onHover = (e) => {
    const cursorAt = timeAtCursor(e)
    if (cursorAt) {
      onHoverCallback(cursorAt, e.event.layerX)
    }
  }

  const onClick = (e) => {
    if (onClickCallback) {
      const closestPoint = closestPointToCursor(e)
      onClickCallback(timeAtCursor(e), closestPoint.bbox.x0)
    }
  }

  const onChartLoad = (element) => {
    graphRef.current = element
    if (updateGraphElementCallback) {
      updateGraphElementCallback(element)
    }

    updateAlerts(selectedLegends?.alerts)
  }

  if (!updatedLayout || !rthData) {
    return (
      <S.NoData>
        <h1>no data</h1>
        <h2>There is no data for this day.</h2>
      </S.NoData>
    )
  } else {
    return (
      <div data-testid={chartDivId}>
        <S.Plot
          divId={chartDivId}
          variant={variant}
          data={allTraces}
          revision={plotlyRevision}
          layout={updatedLayout}
          onHover={(e) => onHover(e, updatedLayout)}
          onUnhover={onUnhoverCallback}
          onClick={(e) => onClick(e, updatedLayout)}
          onDoubleClick={onDoubleClickCallback}
          config={config}
          onRelayout={onRelayoutCallback}
          onInitialized={(_, element) => onChartLoad(element)}
          useResizeHandler
        />

        <AlertsTooltip alerts={alerts} deviceConnectionState={deviceConnectionStateAlerts} />
      </div>
    )
  }
}

RthPlot.propTypes = propTypes
RthPlot.defaultProps = defaultProps

export default RthPlot

const S = {
  NoData: styled(NoData)`
    min-height: 500px;

    @media ${screenSize.small} {
      min-height: 0;
      padding: 3.3em;
    }
  `,
  Plot: styled(PlotlyPlot) < { variant: 'normal' | 'fullscreen' } > `
    width: 100%;
    height: ${({ variant }) => variant === 'normal' ? '400px' : '750px'};
  `
}
